import { useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { formatEuro, formatInteger, formatSquareMeters } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Eyebrow,
  FilterChip,
  Segmented,
  Select,
  Skeleton,
  TextField,
  useToast,
} from '../../../ds/components';
import { useIsCompact, useIsMobile } from '../../../ds/hooks';
import { errorMessage } from '../../../lib/api';
import { downloadFile, unitsQueryKey, updateUnit, useInvalidateProperty, useUnits } from '../api';
import { availabilityPill } from '../availability';
import { StatusPill } from '../bits';
import type { UnitFilter, UnitRow, UnitSort, UnitsResponse } from '../types';
import {
  INLINE_LABELS,
  RELEASE_OPTIONS,
  canEditInline,
  inlineBody,
  inlineDisplay,
  inlineText,
  parseInline,
  patchedRow,
  replaceRow,
  rowFromDetail,
  runInlineSave,
  type InlineField,
} from './unitsInline';
import styles from './UnitsCard.module.css';

type Tab = Exclude<UnitFilter, 'unavailable'>;
type NumericField = Exclude<InlineField, 'salesReleased'>;

/** The counter of a tab in `UnitsResponse.counts` (effective availability, `unitCountBucket`). */
function countOf(counts: UnitsResponse['counts'], tab: Tab): number {
  if (tab === 'all') return counts.all;
  if (tab === 'not_released') return counts.notReleased;
  return counts[tab];
}

export interface UnitsCardProps {
  propertyId: string;
  propertyName: string;
  /** The property may be edited (`PropertyRecord.canEdit`): the inline cells are editable. */
  canEdit: boolean;
  onReserve: (unit: UnitRow) => void;
}

/** „Etage/Lage": floor and building („2. OG · Haus A"), else „—". */
function floorLine(unit: UnitRow): string {
  return [unit.floor, unit.building].filter(Boolean).join(' · ') || '—';
}

const stop = (event: MouseEvent | KeyboardEvent) => event.stopPropagation();

/** Click-to-edit money/share cell: Enter or blur saves, Escape cancels (D-365). */
function NumberCell({
  unit,
  field,
  editable,
  onSave,
}: {
  unit: UnitRow;
  field: NumericField;
  editable: boolean;
  onSave: (unit: UnitRow, field: InlineField, value: number | null) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const label = `${INLINE_LABELS[field]} ${unit.number}`;
  if (!editable) {
    return <span className={styles.numeric}>{inlineDisplay(field, unit)}</span>;
  }
  if (draft === null) {
    return (
      <button
        type="button"
        className={clsx('pressable', styles.cellButton, styles.numeric)}
        aria-label={`${label} bearbeiten`}
        onClick={(event) => {
          stop(event);
          setError(undefined);
          setDraft(inlineText(field, unit));
        }}
      >
        {inlineDisplay(field, unit)}
      </button>
    );
  }
  const commit = async () => {
    const parsed = parseInline(field, draft);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    if (parsed.value === unit[field]) {
      setDraft(null);
      return;
    }
    setDraft(null);
    await onSave(unit, field, parsed.value);
  };
  return (
    <span className={styles.cellEditor} onClick={stop} onKeyDown={stop}>
      <TextField
        label={label}
        labelHidden
        size="compact"
        type={field === 'coOwnershipShare' ? 'text' : 'money'}
        inputMode="decimal"
        autoFocus
        value={draft}
        error={error}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(null);
            setError(undefined);
          }
        }}
      />
    </span>
  );
}

/** Select-on-click „Freigabe" (D-365): the rule of the dialog stays in the option hint. */
function ReleaseCell({
  unit,
  editable,
  onSave,
}: {
  unit: UnitRow;
  editable: boolean;
  onSave: (unit: UnitRow, field: InlineField, value: boolean) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const label = unit.salesReleased ? 'Freigegeben' : 'Nicht freigegeben';
  if (!editable) return <span className={styles.quiet}>{label}</span>;
  if (!open) {
    return (
      <button
        type="button"
        className={clsx('pressable', styles.cellButton)}
        aria-label={`Freigabe ${unit.number} ändern`}
        title="Nur freigegebene Einheiten können reserviert werden."
        onClick={(event) => {
          stop(event);
          setOpen(true);
        }}
      >
        {label}
      </button>
    );
  }
  return (
    <span className={styles.cellEditor} onClick={stop} onKeyDown={stop}>
      <Select
        label={`Freigabe ${unit.number}`}
        labelHidden
        size="compact"
        autoFocus
        value={unit.salesReleased ? 'yes' : 'no'}
        options={RELEASE_OPTIONS}
        hint="Nur freigegebene Einheiten können reserviert werden."
        onChange={(event) => {
          setOpen(false);
          const next = event.target.value === 'yes';
          if (next !== unit.salesReleased) void onSave(unit, 'salesReleased', next);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
          }
        }}
      />
    </span>
  );
}

/** The status cell: the unit status pill, narrowed by sales release / TeamProQ (status itself is derived). */
function StatusCell({ unit }: { unit: UnitRow }) {
  if (unit.availability !== unit.status) {
    const pill = availabilityPill(unit.availability);
    return <StatusPill tone={pill.tone}>{unit.availabilityLabel}</StatusPill>;
  }
  return (
    <StatusPill tone={unit.statusTone} unit>
      {unit.statusLabel}
    </StatusPill>
  );
}

/** Sortable column head (D-366): a button with `aria-sort` on the header cell. */
function SortHead({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: UnitSort['key'];
  sort: UnitSort;
  onSort: (column: UnitSort['key']) => void;
}) {
  const active = sort.key === column;
  return (
    <span
      role="columnheader"
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className={styles.sortButton} onClick={() => onSort(column)}>
        {label}
        <span aria-hidden="true" className={clsx(styles.sortMark, active && styles.sortMarkActive)}>
          {active && sort.direction === 'desc' ? '↓' : '↑'}
        </span>
      </button>
    </span>
  );
}

/**
 * Objekte 03 „Einheiten": tabs, „Preisliste exportieren", the table Bezeichnung · Etage/Lage · m² · Miete Ist ·
 * Miete Soll · MEA · Status · Freigabe · Kaufpreis (D-366; compact without Etage/Lage and MEA) with inline editing of
 * Miete Ist/Soll, MEA and Freigabe (D-365), sorting by Bezeichnung and Status, „7 von 34 Einheiten · Weitere laden".
 */
export function UnitsCard({ propertyId, propertyName, canEdit, onReserve }: UnitsCardProps) {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const compact = useIsCompact() && !mobile;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateProperty();
  const [tab, setTab] = useState<Tab>('all');
  const [reservableOnly, setReservableOnly] = useState(false);
  const [sort, setSort] = useState<UnitSort>({ key: 'number', direction: 'asc' });
  const [exporting, setExporting] = useState(false);
  // „Nur reservierbare": the server filters by availability (`?reservable=true`), paged like the tabs.
  const filter: Tab = reservableOnly ? 'all' : tab;
  const units = useUnits(propertyId, filter, true, reservableOnly, sort);
  const key = unitsQueryKey(propertyId, filter, reservableOnly, sort);
  const items = useMemo(() => units.data?.pages.flatMap((page) => page.items) ?? [], [units.data]);
  const first = units.data?.pages[0];
  const shown = items.length;
  const total = first ? (reservableOnly ? first.counts.reservable : countOf(first.counts, tab)) : 0;
  // the tab counters stay those of the last loaded answer while a tab switch loads
  const counts = first?.counts;
  const tabItems: { value: Tab; label: string }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'available', label: 'Frei' },
    { value: 'reserved', label: 'Reserviert' },
  ];
  if (counts && counts.exclusive > 0) tabItems.push({ value: 'exclusive', label: 'Exklusiv' });
  tabItems.push({ value: 'sold', label: 'Verkauft' });
  if (counts && counts.notReleased > 0) tabItems.push({ value: 'not_released', label: 'Nicht freigegeben' });
  const tabs = tabItems.map((item) => ({
    ...item,
    label: counts ? `${item.label} · ${formatInteger(countOf(counts, item.value))}` : item.label,
  }));

  async function exportPriceList() {
    setExporting(true);
    try {
      const name = await downloadFile(
        `/properties/${propertyId}/price-list.csv`,
        `Preisliste_${propertyName}.csv`,
      );
      toast({ message: `${name} heruntergeladen.` });
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setExporting(false);
    }
  }

  function onSort(column: UnitSort['key']) {
    setSort((current) =>
      current.key === column
        ? { key: column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column, direction: 'asc' },
    );
  }

  /** One inline change: optimistic row, `PATCH /units/:id`, the server's row, or revert + toast. */
  function save(unit: UnitRow, field: InlineField, value: number | boolean | null): Promise<boolean> {
    return runInlineSave({
      apply: () => {
        const snapshot = queryClient.getQueryData<InfiniteData<UnitsResponse>>(key);
        queryClient.setQueryData<InfiniteData<UnitsResponse>>(key, (data) =>
          replaceRow(data, unit.id, (row) => patchedRow(row, field, value)),
        );
        return snapshot;
      },
      save: () => updateUnit(unit.id, inlineBody(field, value)),
      commit: (detail) => {
        queryClient.setQueryData<InfiniteData<UnitsResponse>>(key, (data) =>
          replaceRow(data, unit.id, (row) => rowFromDetail(row, detail)),
        );
        toast({ message: `${INLINE_LABELS[field]} von ${unit.number} gespeichert.` });
        // counts, tabs and the record's figures follow the change
        void invalidate(propertyId);
      },
      revert: (snapshot) => queryClient.setQueryData(key, snapshot),
      fail: (error) => toast({ message: errorMessage(error) }),
    });
  }

  const openUnit = (unit: UnitRow) => void navigate(`/properties/${propertyId}/units/${unit.id}`);
  const openCalculator = (unit: UnitRow) =>
    void navigate(`/properties/${propertyId}/calculator?unitId=${unit.id}`);
  const editable = (unit: UnitRow, field: InlineField) => canEditInline(unit, field, canEdit);

  return (
    <section className={styles.card} aria-labelledby="units-eyebrow">
      <div className={styles.head}>
        <Eyebrow id="units-eyebrow">Einheiten</Eyebrow>
        <span className={styles.summary}>{first ? first.summary : <Skeleton width={120} height={12} />}</span>
        <Segmented<Tab>
          label="Einheiten filtern"
          className={styles.tabs}
          value={reservableOnly ? 'available' : tab}
          items={tabs}
          onChange={(next) => {
            setReservableOnly(false);
            setTab(next);
          }}
        />
        <FilterChip
          label="Nur reservierbare"
          active={reservableOnly}
          onClick={() => setReservableOnly((value) => !value)}
        />
        <button
          type="button"
          className={clsx('pressable', styles.smallButton)}
          onClick={() => void exportPriceList()}
          disabled={exporting}
          aria-busy={exporting || undefined}
        >
          Preisliste exportieren
        </button>
      </div>

      {units.isError ? (
        <div className={styles.state}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void units.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(units.error)}
          </Banner>
        </div>
      ) : (
        <div role="table" aria-label="Einheiten" aria-busy={units.isPending || undefined}>
          {!mobile ? (
            <div className={clsx(styles.tableHead, compact && styles.compactGrid)} role="row">
              <SortHead label="Bezeichnung" column="number" sort={sort} onSort={onSort} />
              {compact ? null : <span role="columnheader">Etage/Lage</span>}
              <span role="columnheader" className={styles.end}>
                m²
              </span>
              <span role="columnheader" className={styles.end}>
                Miete Ist
              </span>
              <span role="columnheader" className={styles.end}>
                Miete Soll
              </span>
              {compact ? null : (
                <span role="columnheader" className={styles.end}>
                  MEA
                </span>
              )}
              <SortHead label="Status" column="status" sort={sort} onSort={onSort} />
              <span role="columnheader">Freigabe</span>
              <span role="columnheader" className={styles.end}>
                Kaufpreis
              </span>
              <span role="columnheader" aria-label="Aktionen" />
            </div>
          ) : null}
          {units.isPending
            ? Array.from({ length: 5 }, (_, index) => (
                <div
                  key={index}
                  className={clsx(styles.row, compact && styles.compactGrid)}
                  aria-hidden="true"
                >
                  <Skeleton width={56} height={14} />
                  {compact ? null : <Skeleton width="70%" height={12} />}
                  <Skeleton width={40} height={14} />
                  <Skeleton width={60} height={14} />
                  <Skeleton width={60} height={14} />
                  {compact ? null : <Skeleton width={40} height={14} />}
                  <Skeleton width={48} height={22} />
                  <Skeleton width={64} height={14} />
                  <Skeleton width={70} height={14} />
                  <span />
                </div>
              ))
            : items.map((unit) => (
                <div
                  key={unit.id}
                  role="row"
                  tabIndex={0}
                  className={clsx(styles.row, compact && styles.compactGrid)}
                  onClick={() => openUnit(unit)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openUnit(unit);
                    }
                  }}
                >
                  <span role="cell" className={styles.number}>
                    {unit.number}
                  </span>
                  {compact ? null : (
                    <span role="cell" className={styles.location}>
                      {floorLine(unit)}
                    </span>
                  )}
                  <span role="cell" className={clsx(styles.numeric, styles.area)}>
                    {unit.areaSqm === null ? '—' : formatSquareMeters(unit.areaSqm)}
                  </span>
                  <span role="cell" className={clsx(styles.endCell, styles.rentActual)}>
                    <NumberCell
                      unit={unit}
                      field="rentActualCents"
                      editable={editable(unit, 'rentActualCents')}
                      onSave={save}
                    />
                  </span>
                  <span role="cell" className={clsx(styles.endCell, styles.rentTarget)}>
                    <NumberCell
                      unit={unit}
                      field="rentTargetCents"
                      editable={editable(unit, 'rentTargetCents')}
                      onSave={save}
                    />
                  </span>
                  {compact ? null : (
                    <span role="cell" className={clsx(styles.endCell, styles.share)}>
                      <NumberCell
                        unit={unit}
                        field="coOwnershipShare"
                        editable={editable(unit, 'coOwnershipShare')}
                        onSave={save}
                      />
                    </span>
                  )}
                  <span role="cell" className={styles.status}>
                    <StatusCell unit={unit} />
                  </span>
                  <span role="cell" className={styles.release}>
                    <ReleaseCell unit={unit} editable={editable(unit, 'salesReleased')} onSave={save} />
                  </span>
                  <span role="cell" className={clsx(styles.numeric, styles.price)}>
                    {unit.priceCents === null ? '—' : formatEuro(unit.priceCents)}
                  </span>
                  <span role="cell" className={styles.actions} onClick={stop}>
                    {unit.actions.calculator ? (
                      <button
                        type="button"
                        className={clsx('pressable', styles.rowButton, styles.rowButtonCalculator)}
                        onClick={() => openCalculator(unit)}
                      >
                        Rechner
                      </button>
                    ) : null}
                    {unit.actions.reserve === 'available' ? (
                      <button
                        type="button"
                        className={clsx('pressable', styles.rowButton, styles.rowButtonPrimary)}
                        onClick={() => onReserve(unit)}
                      >
                        Reservieren
                      </button>
                    ) : unit.actions.reserve === 'view' ? (
                      <button
                        type="button"
                        className={clsx('pressable', styles.rowButton, styles.rowButtonSecondary)}
                        onClick={() => openUnit(unit)}
                      >
                        Reservierung
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={clsx(styles.rowButton, styles.rowButtonDisabled)}
                        disabled
                        aria-disabled="true"
                      >
                        Reservieren
                      </button>
                    )}
                  </span>
                </div>
              ))}
          {!units.isPending && items.length === 0 ? (
            <div className={styles.empty}>
              {reservableOnly ? 'Keine reservierbare Einheit.' : 'Keine Einheiten in dieser Auswahl.'}
            </div>
          ) : null}
          <div className={styles.foot}>
            <span className={styles.footText}>
              {first
                ? reservableOnly
                  ? `${formatInteger(shown)} von ${formatInteger(total)} reservierbaren Einheiten`
                  : `${formatInteger(shown)} von ${formatInteger(total)} Einheiten`
                : ''}
            </span>
            {units.hasNextPage ? (
              <button
                type="button"
                className={clsx('pressable', styles.smallButton)}
                onClick={() => void units.fetchNextPage()}
                disabled={units.isFetchingNextPage}
                aria-busy={units.isFetchingNextPage || undefined}
              >
                Weitere laden
              </button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
